import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { TrendingUp, TrendingDown, Minus, Dot } from "lucide-react";
import { publicApi } from "@/lib/api";
import { fmtMoney, timeAgo } from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * Live rate ticker. Polls the public overview for buy/sell rates every
 * 15 s and animates the pills when a value changes. Designed to sit
 * inside the Trade header; adapts to the dark vault theme.
 */
export default function RateTicker({ className }) {
  const { data } = useQuery({
    queryKey: ["public-overview", "ticker"],
    queryFn: () => publicApi.get("/public/overview").then((r) => r.data),
    refetchInterval: 15000,
    refetchOnWindowFocus: true,
  });
  const rates = data?.rates;
  const prev = useRef({ buy: null, sell: null });
  const [pulse, setPulse] = useState({ buy: "none", sell: "none" });

  useEffect(() => {
    if (!rates) return;
    const nextPulse = { buy: "none", sell: "none" };
    if (prev.current.buy != null && rates.buy_rate !== prev.current.buy) {
      nextPulse.buy = rates.buy_rate > prev.current.buy ? "up" : "down";
    }
    if (prev.current.sell != null && rates.sell_rate !== prev.current.sell) {
      nextPulse.sell = rates.sell_rate > prev.current.sell ? "up" : "down";
    }
    prev.current = { buy: rates.buy_rate, sell: rates.sell_rate };
    setPulse(nextPulse);
    const t = setTimeout(() => setPulse({ buy: "none", sell: "none" }), 1800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rates?.buy_rate, rates?.sell_rate, rates?.updated_at]);

  if (!rates) return null;

  return (
    <div
      className={cn(
        "flex items-center gap-1 overflow-x-auto scrollbar-thin",
        className
      )}
      data-testid="rate-ticker"
      aria-label="Live exchange rates"
    >
      <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-300">
        <Dot className="h-3 w-3 animate-pulse text-emerald-400" /> Live
      </span>
      <TickerPill
        label="Buy USDT"
        value={rates.buy_rate}
        pulse={pulse.buy}
        testId="rate-ticker-buy"
      />
      <TickerPill
        label="Sell USDT"
        value={rates.sell_rate}
        pulse={pulse.sell}
        testId="rate-ticker-sell"
      />
      <TickerPill
        label="Fee"
        value={null}
        display={`${rates.fee_pct ?? 0}%`}
        pulse="none"
        testId="rate-ticker-fee"
      />
      <span className="hidden whitespace-nowrap text-[11px] text-[hsl(var(--vault-ink-muted,220_15%_55%))] sm:inline">
        updated {timeAgo(rates.updated_at)}
      </span>
    </div>
  );
}

function TickerPill({ label, value, display, pulse, testId }) {
  const Icon = pulse === "up" ? TrendingUp : pulse === "down" ? TrendingDown : Minus;
  const tone =
    pulse === "up"
      ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300"
      : pulse === "down"
      ? "border-rose-400/40 bg-rose-400/10 text-rose-300"
      : "border-[hsl(var(--vault-gold,44_86%_60%)/0.3)] bg-[hsl(var(--vault-gold,44_86%_60%)/0.08)] text-[hsl(var(--vault-ink-dim,220_15%_75%))]";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors",
        tone
      )}
      data-testid={testId}
      data-pulse={pulse}
    >
      <span className="uppercase tracking-wider text-[10px] opacity-80">{label}</span>
      <span className="num font-semibold">
        {display ?? (value != null ? fmtMoney(value, "INR") : "\u2014")}
      </span>
      {pulse !== "none" && <Icon className="h-3 w-3" />}
    </span>
  );
}
